import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { Row, Col, Card, Button, message, Spin, Empty, Form, Input, InputNumber } from 'antd';
import QRCode from 'qrcode.react';
import './css/CollarSelectionPage.css';

const { TextArea } = Input;

const CollarSelectionPage = () => {
  const { catId } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [quantities, setQuantities] = useState({});
  const [userAddress, setUserAddress] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch cat details
        const catResponse = await axios.get(`${API_BASE_URL}/cat/${catId}/`);
        setCat(catResponse.data);

        // Fetch all collar products
        const productsResponse = await axios.get(`${API_BASE_URL}/api/business/products/`, {
          params: {
            category: 'collar'
          }
        });
        
        if (productsResponse.data && Array.isArray(productsResponse.data)) {
          setProducts(productsResponse.data);
          // Initialize quantities for all products
          const initialQuantities = {};
          productsResponse.data.forEach(product => {
            initialQuantities[product.id] = 0;
          });
          setQuantities(initialQuantities);
          // Select first product by default
          if (productsResponse.data.length > 0) {
            setSelectedProduct(productsResponse.data[0]);
          }
        } else {
          setError('无法加载项圈数据');
        }

        // Fetch user's shipping address
        try {
          const userResponse = await axios.get(`${API_BASE_URL}/api/user-profile/`, {
            withCredentials: true // Include cookies in the request
          });
          if (userResponse.data.shipping_address) {
            setUserAddress(userResponse.data.shipping_address);
            form.setFieldsValue({ shippingAddress: userResponse.data.shipping_address });
          }
        } catch (error) {
          console.error('Error fetching user address:', error);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.response?.data?.detail || '获取数据失败');
        message.error('获取数据失败');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [catId, form]);

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
  };

  const handleQuantityChange = (productId, value) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: value
    }));
    
    // Update the form's quantity field if this is the selected product
    if (selectedProduct && selectedProduct.id === productId) {
      form.setFieldsValue({ quantity: value });
    }
  };

  const generateProductSummary = () => {
    return Object.entries(quantities)
      .filter(([_, quantity]) => quantity > 0)
      .map(([productId, quantity]) => {
        const product = products.find(p => p.id === parseInt(productId));
        return `${quantity} - ${product.name} - ${product.variation || '默认'} - ${product.sku_code};`;
      })
      .join('\n');
  };

  const calculateTotalAmount = () => {
    return Object.entries(quantities).reduce((total, [productId, quantity]) => {
      const product = products.find(p => p.id === parseInt(productId));
      return total + (product ? product.price * quantity : 0);
    }, 0).toFixed(2);
  };

  const handleOrderSubmit = async (values) => {
    if (!selectedProduct) {
      message.error('请选择项圈');
      return;
    }

    // Validate that at least one product has a quantity greater than 0
    const hasProducts = Object.values(quantities).some(quantity => quantity > 0);
    if (!hasProducts) {
      message.error('请选择至少一个产品的数量');
      return;
    }

    setSubmitting(true);
    try {
      const orderData = {
        product: parseInt(selectedProduct.id),
        cat: parseInt(catId),
        shipping_address: values.shippingAddress,
        total_amount: parseFloat(calculateTotalAmount()),
        notes: values.notes || '',
        product_summary: generateProductSummary()
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/business/orders/`,
        orderData,
        { withCredentials: true } // Include cookies in the request
      );

      message.success(`订单创建成功！订单号: ${response.data.order_number}`);
      navigate('/user-center#my-orders');
    } catch (error) {
      console.error('Error submitting order:', error);
      message.error(error.response?.data?.message || '订单提交失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
        <p>加载中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <Empty
          description={
            <span>
              {error}
              <br />
              <Button type="primary" onClick={() => window.location.reload()}>
                重试
              </Button>
            </span>
          }
        />
      </div>
    );
  }

  const catInfoUrl = `https://mao-mi.com/c/${cat?.id}`;

  return (
    <div className="collar-selection-container">
      <Row gutter={24}>
        {/* Left Column - Cat Info */}
        <Col span={6}>
          <div className="cat-info-section">
            <div className="cat-qrcode">
              <QRCode value={catInfoUrl} size={80} />
            </div>
            <div className="cat-avatar-container">
              <img 
                src={cat?.photos?.[0]} 
                alt={cat?.name} 
                className="cat-avatar" 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/default-cat-avatar.png';
                }}
              />
            </div>
            <div className="cat-details">
              <h2>{cat?.name}</h2>
              <p>ID: {cat?.unique_id}</p>
              <p>品种: {cat?.breed?.name_cn}</p>
              {cat?.birth_date && <p>出生日期: {new Date(cat.birth_date).toLocaleDateString()}</p>}
            </div>
          </div>
        </Col>

        {/* Middle Column - Product Details */}
        <Col span={10}>
          {selectedProduct && (
            <div className="product-details-section">
              <div className="product-main-image">
                <img 
                  src={selectedProduct.image} 
                  alt={selectedProduct.name}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/default-product-image.png';
                  }}
                />
              </div>
              <h2>{selectedProduct.name}</h2>
              <div className="product-price">¥{selectedProduct.price}</div>
              {selectedProduct.variation && (
                <div className="product-variation">规格: {selectedProduct.variation}</div>
              )}
              <div className="product-description">{selectedProduct.description}</div>
            </div>
          )}
        </Col>

        {/* Right Column - SKUs and Order Form */}
        <Col span={8}>
          <div className="right-column-container">
            {/* SKUs Grid */}
            <div className="skus-grid-section">
              <h3>选择款式及数量</h3>
              {products.length > 0 ? (
                <Row gutter={[1, 1]}>
                  {products.map(product => (
                    <Col span={6} key={product.id}>
                      <div 
                        className={`sku-item ${selectedProduct?.id === product.id ? 'selected' : ''}`}
                        onClick={() => handleProductSelect(product)}
                      >
                        <div className='image-wrapper'>
                          <img 
                            src={product.image} 
                            alt={product.name}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/default-product-image.png';
                            }}
                          />
                        </div>
                        <div className="sku-item-price">¥{product.price}</div>
                        <div className="quantity-controls">
                          <Button 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuantityChange(product.id, Math.max(0, quantities[product.id] - 1));
                            }}
                          >
                            -
                          </Button>
                          <span className="quantity-display">{quantities[product.id] || 0}</span>
                          <Button 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuantityChange(product.id, quantities[product.id] + 1);
                            }}
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <Empty description="暂无可用的项圈产品" />
              )}
            </div>

            {/* Order Form */}
            <div className="order-form-section">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleOrderSubmit}
                initialValues={{ 
                  quantity: 1,
                  shippingAddress: userAddress 
                }}
              >
                <Form.Item
                  label="收货地址"
                  name="shippingAddress"
                  rules={[{ required: true, message: '请输入收货地址！' }]}
                >
                  <TextArea
                    rows={4}
                    placeholder="请输入详细的收货地址"
                  />
                </Form.Item>

                <Form.Item
                  label="备注"
                  name="notes"
                >
                  <TextArea
                    rows={2}
                    placeholder="可选：添加订单备注"
                  />
                </Form.Item>

                <div className="order-submit-section">
                  <div className="order-total">
                    总计: ¥{calculateTotalAmount()}
                  </div>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={submitting}
                    size="large"
                    className='submit-order-btn'
                  >
                    确认订单
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default CollarSelectionPage;
